import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { FormCalendar, FormCity, FormCountry, FormInput, FormState } from "@/components/forms/FormControls";
import { Button } from "@/components/ui/button";
import { usePatchDeal } from "@/hooks/useDeals";
import { DealType } from "@/enum/common.enum";

/**
 * Set or correct a deal's property address.
 *
 * This exists because buyer-side deals can now be opened with no property. The
 * backend has always accepted `address` on PATCH, but nothing in the UI ever
 * sent it — so without this a house-hunting deal could never be given the
 * address it eventually acquires, which would make the optional-address intake
 * a one-way trap.
 */
const schema = z.object({
  address: z.string().trim().min(3, "Property address is required."),
  country: z.string().trim().min(2, "Country is required."),
  city: z.string().trim().min(2, "City is required."),
  state: z.string().trim().min(2, "State is required.").transform((value) => value.toUpperCase()),
  zip_code: z.string().trim().min(3, "Postal code is required."),
  listing_url: z.string().trim().max(1000).refine((v) => !v || /^https?:\/\//i.test(v), "Paste the full link, starting with https://"),
  listed_at: z.string(),
}).superRefine((values, ctx) => {
  if (values.country === "US" && !/^\d{5}(?:-\d{4})?$/.test(values.zip_code)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["zip_code"], message: "Enter a valid ZIP code." });
  }
});

/** "street, city, STATE zip" — the display string the wizard also stores. */
const composeAddress = (street: string, city: string, state: string, zip: string) =>
  [street, city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");

/**
 * `address` is stored as the composed display string, so the street field must
 * get the street back out of it — otherwise every save appends city/state/zip
 * again. Strips repeats too, so a deal already corrupted that way heals on save.
 */
const streetFromAddress = (deal: Record<string, any>) => {
  let street = deal.address || "";
  const suffix = ", " + composeAddress("", deal.city || "", deal.state || "", deal.zip_code || "");
  if (suffix === ", ") return street;
  while (street.endsWith(suffix)) street = street.slice(0, -suffix.length);
  return street;
};

interface DealAddressDialogProps {
  deal: Record<string, any>;
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export default function DealAddressDialog({ deal, open, onClose, onSaved }: DealAddressDialogProps) {
  const { mutate: patchDeal, isPending } = usePatchDeal();
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      // A placeholder is not a real address, so it is never pre-filled into the
      // field the agent is about to correct.
      address: deal.address_is_placeholder ? "" : streetFromAddress(deal),
      country: deal.country || "US",
      city: deal.city || "",
      state: deal.state || "",
      zip_code: deal.zip_code || "",
      listing_url: deal.listing_url || "",
      listed_at: deal.listed_at || "",
    },
  });
  const isSell = deal.deal_type === DealType.SELL;

  const submit = (values: Record<string, any>) => {
    if (isPending) return;
    // Stored as the same display string the wizard composes, so a deal that
    // gains its address later is indistinguishable from one that had it at
    // intake.
    const address = composeAddress(values.address, values.city, values.state, values.zip_code);
    patchDeal(
      {
        dealId: deal.id,
        payload: {
          address, city: values.city, state: values.state, zip_code: values.zip_code,
          // "" clears the link; an empty date is left alone rather than erased.
          ...(isSell ? { listing_url: values.listing_url, ...(values.listed_at ? { listed_at: values.listed_at } : {}) } : {}),
        },
      },
      { onSuccess: () => { onClose(); onSaved?.(); } }
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="sm:max-w-[520px]" data-testid="deal-address-modal">
        <DialogHeader>
          <DialogTitle>Property address</DialogTitle>
          <DialogDescription>
            Once you have a property, add it here. It replaces the placeholder everywhere —
            the board, your client&apos;s portal, and any email that names this deal.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-3" noValidate>
            <FormInput name="address" label="Property address" placeholder="e.g. 412 Linden Avenue" autoComplete="street-address" data-testid="deal-address-street" required />
            <FormCountry name="country" label="Country" data-testid="deal-address-country" required />
            <div className="grid gap-3 sm:grid-cols-2">
              <FormState name="state" countryName="country" label="State" data-testid="deal-address-state" required />
              <FormCity name="city" stateName="state" countryName="country" label="City" data-testid="deal-address-city" required />
            </div>
            <div className="sm:max-w-[calc(50%_-_0.5rem)]">
              <FormInput name="zip_code" label="Postal code" autoComplete="postal-code" placeholder="11217" data-testid="deal-address-zip" required />
            </div>
            {isSell && (
              <div className="mt-2 flex flex-col gap-3 border-t border-dash-border pt-4">
                <FormInput name="listing_url" label="Listing link" type="url" placeholder="https://www.zillow.com/homedetails/…" description="Your client sees a “View your listing” button on their page." data-testid="deal-listing-url" />
                <div className="sm:max-w-[calc(50%_-_0.5rem)]">
                  <FormCalendar name="listed_at" label="Listed on" description="Days on market counts from this date." data-testid="deal-listed-at" />
                </div>
              </div>
            )}
            <DialogFooter className="mt-2">
              <Button type="button" variant="dashboardOutline" onClick={onClose} disabled={isPending}>Cancel</Button>
              <Button type="submit" variant="dashboardPrimary" loading={isPending} data-testid="deal-address-save">Save address</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
